import { useEffect, useMemo, useState } from 'react';
import { IconArrowLeft } from '@shared/ui/icons';
import { useAgentPermission } from '@entities/user';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import type {
  LogistDeliveryAcceptance,
  LogistMatchCandidate,
} from '@entities/logistics';
import {
  useListLogistOrdersQuery,
  useListLogistTripsQuery,
  useListLogistRegistryQuery,
  useListLogistEtrnTitlesQuery,
  useGetLogistEvidenceQuery,
  useArchiveLogistEvidenceMutation,
  useAcceptLogistDeliveryMutation,
  useSubmitLogistEtrnMutation,
  useRefreshLogistEtrnMutation,
  useRevokeLogistEtrnMutation,
  useExplainLogistEtrnMutation,
  useListLogistInboxQuery,
  useSkipLogistInboxMutation,
  useLinkLogistInboxMutation,
  useAutomatchLogistInboxMutation,
} from '@shared/api';
import {
  Wrap,
  Back,
  Title,
  Subtitle,
  Notice,
  Section,
  SectionTitle,
  NoAccessNotice,
  SubNotice,
  NoticeBtnRow,
  SmallBtn,
  SubCardSubtitle,
} from './styles';
import { apiErr, tripLabel } from './helpers';
import { LogistSubmitSection } from './LogistSubmitSection';
import { LogistRegistryTable } from './LogistRegistryTable';
import { LogistEtrnTitlesCard } from './LogistEtrnTitlesCard';
import { LogistInboxTable } from './LogistInboxTable';
import { LogistDeliveryAcceptanceCard } from './LogistDeliveryAcceptanceCard';
import { LogistMatchCandidatesCard } from './LogistMatchCandidatesCard';

/**
 * ОСНОВА.Логист — реестр ЭТрН и обмен с оператором ИС ЭПД (ТЗ AI-LOGIST-2026
 * §7.4, §1.3). Отправка ЭТрН по рейсу через адаптер оператора (Mock до выбора
 * оператора), реестр со сверкой юр-статуса, опрос/аннулирование. Обмен гейтится
 * OSNOVA_LOGIST_ENABLED (release-gate); при выключенном контуре реестр доступен
 * на чтение, а действия отправки/опроса/отзыва заблокированы. Юр-статус —
 * ДОСЛОВНО от оператора, наш код его не вычисляет.
 */
export function LogistRegistryView() {
  const { can_read, can_write } = useAgentPermission('logist');
  const { activeProjectId, setActiveProject } = useActiveProjectContext();
  const [orderId, setOrderId] = useState('');
  const [tripId, setTripId] = useState('');
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data: ordersData } = useListLogistOrdersQuery(
    activeProjectId ? { project_id: activeProjectId } : undefined,
  );
  const { data: tripsData } = useListLogistTripsQuery(orderId ? { order_id: orderId } : {}, {
    skip: !orderId,
  });

  useEffect(() => {
    if (!orderId) return;
    const ord = ordersData?.orders.find((o) => o.id === orderId);
    if (ord?.project_id) {
      setActiveProject(ord.project_id);
    }
  }, [orderId, ordersData?.orders, setActiveProject]);
  const { data: registryData } = useListLogistRegistryQuery();
  const { data: inboxData } = useListLogistInboxQuery();

  const [submitEtrn, { isLoading: submitting }] = useSubmitLogistEtrnMutation();
  const [refreshEtrn] = useRefreshLogistEtrnMutation();
  const [revokeEtrn] = useRevokeLogistEtrnMutation();
  const [skipInbox] = useSkipLogistInboxMutation();
  const [linkInbox] = useLinkLogistInboxMutation();
  const [automatch] = useAutomatchLogistInboxMutation();
  const [explainEtrn, { isLoading: explaining }] = useExplainLogistEtrnMutation();

  // Какой документ раскрыт в панели титулов. Грузим по требованию: цепочка
  // нужна при разборе «почему документ не закрыт», а не в каждом списке.
  const [titlesFor, setTitlesFor] = useState<string | null>(null);
  const { data: titlesData, isFetching: titlesLoading } = useListLogistEtrnTitlesQuery(
    titlesFor ?? '',
    { skip: !titlesFor },
  );

  // Форма приёмки груза по входящему документу (титул грузополучателя).
  const [deliveryFor, setDeliveryFor] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<LogistDeliveryAcceptance>({ operation: 'full' });
  const [acceptDelivery, { isLoading: accepting }] = useAcceptLogistDeliveryMutation();

  // Доказательный архив показываем рядом с титулами — это один и тот же
  // вопрос «что у нас есть по этому документу».
  const { data: evidenceData } = useGetLogistEvidenceQuery(titlesFor ?? '', { skip: !titlesFor });
  const [archiveEvidence, { isLoading: archiving }] = useArchiveLogistEvidenceMutation();

  const [matchFor, setMatchFor] = useState<string | null>(null);
  const [matchCands, setMatchCands] = useState<LogistMatchCandidate[]>([]);
  const [explanation, setExplanation] = useState<string | null>(null);

  const orders = ordersData?.orders ?? [];
  const trips = tripsData?.trips ?? [];
  const registry = registryData?.etrn ?? [];
  const inbox = inboxData?.inbox ?? [];
  const enabled = registryData?.enabled ?? false;

  const tripName = useMemo(() => {
    const m = new Map(trips.map((t) => [t.id, tripLabel(t)]));
    return (id: string) => m.get(id) ?? id.slice(0, 8);
  }, [trips]);

  async function runArchive(id: string) {
    setErr(null);
    setOk(null);
    try {
      const res = await archiveEvidence(id).unwrap();
      const mb = (res.evidence.size_bytes / 1024 / 1024).toFixed(2);
      setOk(`Комплект документов сохранён (${mb} МБ).`);
    } catch (e) {
      setErr(`Не удалось выгрузить комплект: ${apiErr(e)}`);
    }
  }

  async function sendDelivery() {
    if (!deliveryFor) return;
    setErr(null);
    setOk(null);
    try {
      const res = await acceptDelivery({ id: deliveryFor, acceptance: delivery }).unwrap();
      setOk(
        res.already_submitted
          ? 'Груз по этому документу уже принимали — повторная приёмка не выпускалась.'
          : `Приёмка оформлена черновиком (${res.operator_status}). Подпишите её в кабинете оператора.`,
      );
      setDeliveryFor(null);
      setDelivery({ operation: 'full' });
    } catch (e) {
      setErr(`Не удалось оформить приёмку: ${apiErr(e)}`);
    }
  }

  async function submit() {
    setErr(null);
    setOk(null);
    if (!tripId) {
      setErr('Выберите рейс.');
      return;
    }
    try {
      const res = await submitEtrn(tripId).unwrap();
      setOk(
        res.resubmitted
          ? `ЭТрН отправлен: ${res.etrn.external_id ?? '—'} (${res.etrn.operator_status ?? '—'}).`
          : `ЭТрН уже существует: ${res.etrn.external_id ?? res.etrn.state}.`,
      );
    } catch (e) {
      setErr(`Не удалось отправить: ${apiErr(e)}`);
    }
  }

  async function runAutomatch(id: string) {
    setErr(null);
    setOk(null);
    setMatchFor(null);
    setMatchCands([]);
    try {
      const res = await automatch(id).unwrap();
      if (res.linked) {
        setOk('Найдено сильное совпадение — связано автоматически.');
      } else if (res.candidates.length > 0) {
        setMatchFor(id);
        setMatchCands(res.candidates);
      } else {
        setErr('Подходящих поставок/приёмок не найдено.');
      }
    } catch (e) {
      setErr(`Не удалось подобрать: ${apiErr(e)}`);
    }
  }

  async function runExplain(id: string) {
    setErr(null);
    setOk(null);
    setExplanation(null);
    try {
      const res = await explainEtrn(id).unwrap();
      if (res.explanation) {
        setExplanation(res.explanation);
      } else {
        setErr('Подсказка недоступна: у ЭТрН нет ошибки оператора либо ассистент не ответил.');
      }
    } catch (e) {
      setErr(`Не удалось получить пояснение: ${apiErr(e)}`);
    }
  }

  async function linkCandidate(inboxId: string, c: LogistMatchCandidate) {
    setErr(null);
    setOk(null);
    try {
      await linkInbox({ id: inboxId, linked_kind: c.kind, linked_id: c.id }).unwrap();
      setMatchFor(null);
      setMatchCands([]);
      setOk('Входящий ЭТрН связан.');
    } catch (e) {
      setErr(`Не удалось связать: ${apiErr(e)}`);
    }
  }

  if (!can_read) {
    return (
      <Wrap>
        <NoAccessNotice>
          Нет доступа к разделу «ОСНОВА.Логист».
        </NoAccessNotice>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Back to="/agents/logist">
        <IconArrowLeft size={16} />
        <span>ОСНОВА.Логист</span>
      </Back>
      <Title>Реестр ЭТрН</Title>
      <Subtitle>
        Формирование электронных транспортных накладных по рейсам через оператора ИС ЭПД и сверка
        их юридического статуса. Юр-статус приходит от оператора — наш код его не вычисляет. XML
        (формат 1065@) собирает оператор. При включённом контуре фоновый обмен сам дожимает
        отправку с повторами и периодически освежает статусы.
      </Subtitle>

      {!enabled && (
        <Notice>
          Контур обмена с оператором выключен (release-gate §1.3): переменная{' '}
          <code>OSNOVA_LOGIST_ENABLED</code> не установлена. Реестр доступен только на чтение;
          отправка, опрос статуса и аннулирование заблокированы до включения контура.
        </Notice>
      )}

      {can_write && (
        <LogistSubmitSection
          orderId={orderId}
          setOrderId={setOrderId}
          tripId={tripId}
          setTripId={setTripId}
          orders={orders}
          trips={trips}
          submitting={submitting}
          enabled={enabled}
          onSubmit={submit}
          ok={ok}
          err={err}
        />
      )}

      <Section>
        <SectionTitle>Реестр{registry.length > 0 ? ` · ${registry.length}` : ''}</SectionTitle>
        <LogistRegistryTable
          registry={registry}
          tripName={tripName}
          can_write={can_write}
          enabled={enabled}
          explaining={explaining}
          titlesFor={titlesFor}
          onRefresh={(id) =>
            refreshEtrn(id)
              .unwrap()
              .then(() => setOk('Статус обновлён.'))
              .catch((x) => setErr(`Не удалось: ${apiErr(x)}`))
          }
          onRevoke={(id) =>
            revokeEtrn({ id })
              .unwrap()
              .then(() => setOk('ЭТрН аннулирован.'))
              .catch((x) => setErr(`Не удалось: ${apiErr(x)}`))
          }
          onExplain={runExplain}
          onToggleTitles={(id) => setTitlesFor(titlesFor === id ? null : id)}
        />
        {titlesFor && (
          <LogistEtrnTitlesCard
            titlesFor={titlesFor}
            evidence={evidenceData?.evidence}
            titles={titlesData?.titles}
            titlesLoading={titlesLoading}
            archiving={archiving}
            can_write={can_write}
            enabled={enabled}
            onClose={() => setTitlesFor(null)}
            onArchive={runArchive}
          />
        )}
        {explanation && (
          <SubNotice>
            <strong>Пояснение ассистента.</strong> {explanation}
            <NoticeBtnRow>
              <SmallBtn onClick={() => setExplanation(null)}>Закрыть</SmallBtn>
            </NoticeBtnRow>
          </SubNotice>
        )}
      </Section>

      <Section>
        <SectionTitle>Входящие ЭТрН{inbox.length > 0 ? ` · ${inbox.length}` : ''}</SectionTitle>
        <SubCardSubtitle>
          ЭТрН, где САТКОМ — грузополучатель. Принимаются идемпотентно от оператора; связка с
          поставкой МТО и приёмкой Прораба — вручную/матчингом (Волна 2).
        </SubCardSubtitle>
        <LogistInboxTable
          inbox={inbox}
          can_write={can_write}
          enabled={enabled}
          onOpenDelivery={(i) => {
            setDeliveryFor(deliveryFor === i.id ? null : i.id);
            setDelivery({ operation: 'full', gross_mass_kg: i.net_mass_kg });
          }}
          onAutomatch={runAutomatch}
          onSkip={(id) =>
            skipInbox(id)
              .unwrap()
              .then(() => setOk('Входящий ЭТрН помечен пропущенным.'))
              .catch((x) => setErr(`Не удалось: ${apiErr(x)}`))
          }
        />

        {deliveryFor && (
          <LogistDeliveryAcceptanceCard
            delivery={delivery}
            setDelivery={setDelivery}
            accepting={accepting}
            onClose={() => setDeliveryFor(null)}
            onSubmit={sendDelivery}
          />
        )}

        {matchFor && (
          <LogistMatchCandidatesCard
            matchFor={matchFor}
            matchCands={matchCands}
            onClose={() => setMatchFor(null)}
            onLink={linkCandidate}
          />
        )}
      </Section>
    </Wrap>
  );
}
